import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  buscarBancosBrasileiros,
  buscarContasDominio,
  buscarContasProtheus,
} from "../../services/api-conciliacao.service";
import AutoCompleteEmpresaCnpj from "../../components/AutoCompleteEmpresaCnpj";

type ContaOption = {
  id_conciliacao_conta_contabil: number;
  conta_contabil: string;
  descricao: string;
};

type BancoOption = {
  id_conciliacao_banco_brasileiro: number;
  codigo: string;
  nome: string;
  label: string;
};

function contaLabel(c: ContaOption | null) {
  if (!c) return "";
  return `${c.conta_contabil} - ${c.descricao}`;
}

function normalizarCnpjDigitos(valor: string): string {
  return valor.replace(/\D/g, "");
}

function normalizarListaCnpjs(valores: unknown): string[] {
  if (!Array.isArray(valores)) return [];
  return valores
    .map((c) => normalizarCnpjDigitos(String(c)))
    .filter((d) => d.length === 14);
}

const buildBancoFiltrosFormSchema = (permiteTodosCnpj: boolean) =>
  z.object({
    id_conciliacao_banco_brasileiro: z
      .number({ invalid_type_error: "Selecione o banco" })
      .min(1, "Selecione o banco"),
    filtro: z.string().min(1, "Informe o filtro"),
    cnpjs: z.preprocess(
      (val) => (Array.isArray(val) ? val : []),
      permiteTodosCnpj
        ? z.array(z.string())
        : z.array(z.string()).min(1, "Selecione ao menos uma empresa (CNPJ)."),
    ),
    id_conciliacao_conta_dominio: z
      .number({ invalid_type_error: "Selecione a conta domínio" })
      .min(1, "Selecione a conta domínio"),
    id_conciliacao_conta_protheus: z.number().nullable(),
  });

type BancoFiltrosFormData = z.infer<ReturnType<typeof buildBancoFiltrosFormSchema>>;

type BancoFiltrosFormProps = {
  bancoFiltrosValues: Record<string, unknown>;
  onSubmit(data: unknown): void;
  permiteTodosCnpj?: boolean;
};

export const BANCO_FILTROS_FORM_ID = "banco-filtros-vinculo-form";

const autocompletePopperProps = { disablePortal: true };

export default function BancoFiltrosForm({
  bancoFiltrosValues,
  onSubmit,
  permiteTodosCnpj = false,
}: BancoFiltrosFormProps) {
  const [contasDominio, setContasDominio] = useState<ContaOption[]>([]);
  const [contasProtheus, setContasProtheus] = useState<ContaOption[]>([]);
  const [bancosBr, setBancosBr] = useState<BancoOption[]>([]);
  const [loadingContas, setLoadingContas] = useState(true);

  const resolver = useMemo(
    () => zodResolver(buildBancoFiltrosFormSchema(permiteTodosCnpj)),
    [permiteTodosCnpj],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<BancoFiltrosFormData>({
    mode: "onChange",
    resolver,
    defaultValues: {
      id_conciliacao_banco_brasileiro:
        (bancoFiltrosValues.id_conciliacao_banco_brasileiro as number | undefined) ?? 0,
      filtro: (bancoFiltrosValues.filtro as string) ?? "",
      cnpjs: normalizarListaCnpjs(bancoFiltrosValues.cnpjs),
      id_conciliacao_conta_dominio:
        (bancoFiltrosValues.id_conciliacao_conta_dominio as number | undefined) ?? 0,
      id_conciliacao_conta_protheus:
        (bancoFiltrosValues.id_conciliacao_conta_protheus as number | null | undefined) ?? null,
    },
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingContas(true);
      try {
        const [domRes, protRes, bRes] = await Promise.all([
          buscarContasDominio(),
          buscarContasProtheus(),
          buscarBancosBrasileiros(),
        ]);
        if (!cancelled) {
          setContasDominio(domRes.contasContabil ?? []);
          setContasProtheus(protRes.contasContabil ?? []);
          const raw = (bRes.bancosBrasileiros ?? []) as BancoOption[];
          setBancosBr(
            raw.map((b) => ({
              ...b,
              label:
                b.label ??
                `${String(b.codigo ?? "").trim()} - ${b.nome ?? ""}`,
            })),
          );
        }
      } catch {
        if (!cancelled) {
          setContasDominio([]);
          setContasProtheus([]);
          setBancosBr([]);
        }
      } finally {
        if (!cancelled) setLoadingContas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    reset({
      id_conciliacao_banco_brasileiro:
        (bancoFiltrosValues.id_conciliacao_banco_brasileiro as number | undefined) ?? 0,
      filtro: (bancoFiltrosValues.filtro as string) ?? "",
      cnpjs: normalizarListaCnpjs(bancoFiltrosValues.cnpjs),
      id_conciliacao_conta_dominio:
        (bancoFiltrosValues.id_conciliacao_conta_dominio as number | undefined) ?? 0,
      id_conciliacao_conta_protheus:
        (bancoFiltrosValues.id_conciliacao_conta_protheus as number | null | undefined) ?? null,
    });
  }, [bancoFiltrosValues, reset]);

  const submit = (data: BancoFiltrosFormData) => {
    const isEdit = bancoFiltrosValues.id_conciliacao_filtro_vinculo_conta != null;
    const filtroVinculoConta: Record<string, unknown> = {
      idConciliacaoBancoBrasileiro: data.id_conciliacao_banco_brasileiro,
      filtro: data.filtro,
      filtroGlobal: false,
    };

    if (!isEdit || dirtyFields.cnpjs) {
      filtroVinculoConta.cnpjs = normalizarListaCnpjs(data.cnpjs);
    }
    if (!isEdit || dirtyFields.id_conciliacao_conta_dominio) {
      filtroVinculoConta.idConciliacaoContaDominio = data.id_conciliacao_conta_dominio;
    }
    if (!isEdit || dirtyFields.id_conciliacao_conta_protheus) {
      filtroVinculoConta.idConciliacaoContaProtheus = data.id_conciliacao_conta_protheus;
    }

    onSubmit({ filtroVinculoConta });
  };

  const optionDominio = (id: number) =>
    id > 0 ? contasDominio.find((c) => c.id_conciliacao_conta_contabil === id) ?? null : null;
  const optionProtheus = (id: number | null) =>
    id == null ? null : contasProtheus.find((c) => c.id_conciliacao_conta_contabil === id) ?? null;

  const optionBanco = (id: number) =>
    id > 0 ? bancosBr.find((b) => b.id_conciliacao_banco_brasileiro === id) ?? null : null;

  return (
    <form id={BANCO_FILTROS_FORM_ID} onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <Box
        className={styles.formContainer}
        sx={{ px: 3, pt: 0.5, pb: 2, width: "100%", boxSizing: "border-box" }}
      >
        <Box className={styles.formContent}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Controller
                name="id_conciliacao_banco_brasileiro"
                control={control}
                render={({ field }) => (
                  <Autocomplete<BancoOption, false, false, false>
                    loading={loadingContas}
                    options={bancosBr}
                    componentsProps={{ popper: autocompletePopperProps }}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) =>
                      a.id_conciliacao_banco_brasileiro === b.id_conciliacao_banco_brasileiro
                    }
                    value={optionBanco(field.value)}
                    onChange={(_, v) =>
                      field.onChange(v ? v.id_conciliacao_banco_brasileiro : 0)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Banco"
                        error={Boolean(errors.id_conciliacao_banco_brasileiro)}
                        helperText={errors.id_conciliacao_banco_brasileiro?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="filtro"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <TextField
                    fullWidth
                    label="Filtro"
                    error={Boolean(errors.filtro)}
                    helperText={errors.filtro?.message}
                    placeholder="Filtro"
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="id_conciliacao_conta_dominio"
                control={control}
                render={({ field }) => (
                  <Autocomplete<ContaOption, false, false, false>
                    loading={loadingContas}
                    options={contasDominio}
                    componentsProps={{ popper: autocompletePopperProps }}
                    getOptionLabel={(o) => contaLabel(o)}
                    isOptionEqualToValue={(a, b) =>
                      a.id_conciliacao_conta_contabil === b.id_conciliacao_conta_contabil
                    }
                    value={optionDominio(field.value)}
                    onChange={(_, v) => field.onChange(v ? v.id_conciliacao_conta_contabil : 0)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Conta Domínio"
                        error={Boolean(errors.id_conciliacao_conta_dominio)}
                        helperText={errors.id_conciliacao_conta_dominio?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="id_conciliacao_conta_protheus"
                control={control}
                render={({ field }) => (
                  <Autocomplete<ContaOption, false, false, false>
                    loading={loadingContas}
                    options={contasProtheus}
                    componentsProps={{ popper: autocompletePopperProps }}
                    getOptionLabel={(o) => contaLabel(o)}
                    isOptionEqualToValue={(a, b) =>
                      a.id_conciliacao_conta_contabil === b.id_conciliacao_conta_contabil
                    }
                    value={optionProtheus(field.value)}
                    onChange={(_, v) => field.onChange(v ? v.id_conciliacao_conta_contabil : null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Conta Protheus"
                        error={Boolean(errors.id_conciliacao_conta_protheus)}
                        helperText={errors.id_conciliacao_conta_protheus?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} className={styles.cnpjsField}>
              <Controller
                name="cnpjs"
                control={control}
                render={({ field }) => (
                  <AutoCompleteEmpresaCnpj
                    multiple
                    embedded
                    disablePortal
                    variant="outlined"
                    size="medium"
                    value={normalizarListaCnpjs(field.value)}
                    onChange={(v) => field.onChange(normalizarListaCnpjs(v))}
                    error={Boolean(errors.cnpjs)}
                    helperText={
                      errors.cnpjs?.message ??
                      (permiteTodosCnpj
                        ? "Vazio = vínculo genérico para todas as empresas daquele banco (conciliação por CNPJ na etapa 2)."
                        : "Selecione ao menos uma empresa (CNPJ).")
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </form>
  );
}
